import React, { useState, KeyboardEvent } from 'react'
import classnames from 'classnames'

export type SwitchSize = 'small' | 'medium' | 'large'
export type SwitchColorScheme =
  | 'pitaya'
  | 'acai'
  | 'starfruit'
  | 'rambutan'
  | 'boulder'

interface SwitchProps {
  defaultChecked?: boolean
  size?: SwitchSize
  colorScheme?: SwitchColorScheme
  onLabel?: string
  offLabel?: string
  onChange?: (checked: boolean) => void
  disabled?: boolean
  loading?: boolean
  error?: string
  className?: string
  style?: React.CSSProperties
  id?: string
  'aria-label'?: string
  focusState?: boolean
}

const sizeClasses = {
  small: 'text-sm h-[22px]',
  medium: 'text-lg h-[28px]',
  large: 'text-xl h-[34px]',
}

const colorSchemeClasses = {
  acai: 'bg-acai-100',
  pitaya: 'bg-pitaya-100',
  starfruit: 'bg-starfruit-100',
  rambutan: 'bg-rambutan-100',
  boulder: 'bg-black-20',
}

const sizeToCirlceClasses = {
  small: 'w-[16px] h-[16px]',
  medium: 'w-[22px] h-[22px]',
  large: 'w-[28px] h-[28px]',
}

const sizeToLabelClasses = {
  small: (checked) => (checked ? 'pr-[24px] pl-[12px]' : 'pl-[24px] pr-[12px]'),
  medium: (checked) =>
    checked ? 'pr-[36px] pl-[18px]' : 'pl-[36px] pr-[18px]',
  large: (checked) => (checked ? 'pr-[48px] pl-[24px]' : 'pl-[48px] pr-[24px]'),
}

const colorToLabelClasses = {
  boulder: (checked) => (checked ? 'text-black-80' : 'text-black-80'),
  acai: (checked) => (checked ? 'text-white' : 'text-black-80'),
  pitaya: (checked) => (checked ? 'text-white' : 'text-black-80'),
  starfruit: (checked) => (checked ? 'text-white' : 'text-black-80'),
  rambutan: (checked) => (checked ? 'text-white' : 'text-black-80'),
}

export const Switch: React.FC<SwitchProps> = ({
  defaultChecked = false,
  size = 'small',
  colorScheme = 'boulder',
  onLabel = 'On',
  offLabel = 'Off',
  onChange,
  disabled = false,
  loading = false,
  error,
  className = 'mx-2',
  'aria-label': ariaLabel,
  focusState = false,
  id,
}) => {
  const [checked, setChecked] = useState(defaultChecked)

  const handleToggle = () => {
    if (disabled || loading) return
    const newChecked = !checked

    setChecked(newChecked)
    onChange?.(newChecked)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (disabled || loading) return

    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      handleToggle()
    }
  }

  return (
    <div
      className={classnames(
        'flex items-center cursor-pointer',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        loading ? 'opacity-70 cursor-wait' : '',
        className
      )}
    >
      <div
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        aria-busy={loading}
        aria-label={ariaLabel}
        tabIndex={disabled ? -1 : 0}
        id={id}
        className={classnames(
          'relative w-auto rounded-full transition-colors duration-200',
          focusState
            ? 'focus:outline-none focus:ring-2 focus:ring-offset-2'
            : '',
          !disabled && !loading ? 'hover:opacity-80' : '',
          checked ? colorSchemeClasses[colorScheme] : 'bg-black-20',
          error ? 'ring-2 ring-error' : '',
          sizeClasses[size]
        )}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <div
          className={classnames(
            'absolute top-[3px] rounded-full bg-white shadow-sm',
            'transition-transform duration-200',
            checked ? 'right-[3px] left-unset' : 'left-[3px] right-unset',
            loading ? 'animate-pulse' : '',
            sizeToCirlceClasses[size]
          )}
        />

        <span
          className={classnames(
            'relative h-full flex items-center justify-center select-none text-lg lowercase',
            'transition-colors duration-200',
            colorToLabelClasses[size]?.(checked),
            sizeToLabelClasses[size]?.(checked)
          )}
        >
          {checked ? onLabel : offLabel}
        </span>
      </div>
      {error && <p className="text-error text-xs mt-1">{error}</p>}
    </div>
  )
}
